import { Tile } from '@/core/models/tile';
import { CurrentUser } from '@/core/models/user';
import { ActivityDashboadVmMapper } from './activity-dashboard-vm-mapper';
import { ActivityFiltersFormPageHack, DashboardActivityVM } from '@/core/models/activity';
import { ActivatedRoute, Router } from '@angular/router';
import { Layer } from '@/core/models/layer';
import { TileService } from '@/core/services/open-api/tile.service';
import { firstValueFrom, Subscription } from 'rxjs';
import { LoadingService, PageLoadingState } from '@/core/services/loading.service';
import { dateRangeType } from '@/core/models/date-range';
import { DateRangeHelper } from '@/core/services/utils/date-range-helper';
import {
    applyThemeColorsToCharts,
    BarChartIntervalOptions,
    BarDataType,
    ChartOptions,
    ChartVM,
    PieDataType,
} from './chart-vm';
import { LayerService } from '@/core/services/open-api/layer.service';
import { ActivityService } from '@/core/services/open-api/activity.service';
import { UserService } from '@/core/services/open-api/user.service';
import { Component, OnInit, OnDestroy } from '@angular/core';
import moment from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { SelectItem } from 'primeng/api';
import { SharedFunctions } from '@/core/utils/shared-functions';
import { ThemeService } from '@/core/services/theme.service';
import {
    DateRangeChangedEvent,
    quickSelectRangeType,
} from '@/common/shared/date-range-selector/date-range-selector.component';
import { type } from 'arktype';
import { LocalStorageService } from '@/core/services/utils/local-storage.service';
import { UserPermission } from '@/core/models/account';
import { InitializationManager } from '@/core/utils/initialization-manager';
import { PageDataState } from '@/common/shared/shared-types';

@Component({
    selector: 'app-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss'],
    standalone: false,
})
export class DashboardComponent implements OnInit, OnDestroy {
    stateOptions: SelectItem<DashboardViewMode>[] = [];
    viewState: DashboardView = {
        dateRange: DateRangeHelper.default(),
        dateRangeOption: 'last30days',
        selectedStateOption: 'Me',
    };
    viewStateInitialized = false;
    firstDayOfWeek = 0;
    totalTime: number = 0;
    totalTimeName = '-';
    topLayerName = '';
    stackedOptions = ChartOptions.stackedOptions;
    pieOptions = ChartOptions.pieOptions;
    barData?: BarDataType;
    pieData?: PieDataType;
    displayAppDownloadModal: boolean = false;
    todayUserData: DashboardActivityVM[] = [];
    layers: Layer[] = [];
    tiles: Tile[] = [];
    currentUser?: CurrentUser;
    accountUserIds: number[] = [];
    licenseStatePro = false;
    userDateFormat = '';
    barChartInterval: SelectItem[] = [];
    barChartIntervalSelected: BarChartIntervalOptions = 'DAY';
    subscriptions = new Subscription();
    initMgr: InitializationManager;
    dataState: PageDataState = 'loading';

    constructor(
        public readonly userService: UserService,
        private readonly activityService: ActivityService,
        private readonly layerService: LayerService,
        private readonly loadingService: LoadingService,
        private readonly tileService: TileService,
        private route: ActivatedRoute,
        private router: Router,
        private readonly translate: TranslateService,
        private readonly themeService: ThemeService,
    ) {
        this.initMgr = new InitializationManager(this.loadingService, this.constructor.name);
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
        this.initMgr.destroy();
    }

    ngOnInit(): void {
        this.loadingService.setLoadingState('initializing', this.constructor.name);

        this.displayAppDownloadModal = this.route.snapshot.queryParamMap.get('firstStart') === 'true';

        const t = ['standard_week', 'standard_day', 'standard_month', 'standard_year', 'standard_me', 'standard_team'];
        this.initMgr
            .addDependency(this.translate.get(t), {
                name: 'translations',
                callback: (translations) => {
                    this.stateOptions = [
                        { label: translations.standard_me, value: 'Me' },
                        { label: translations.standard_team, value: 'Team' },
                    ];
                    this.barChartInterval = [
                        { label: translations.standard_day, value: 'DAY' },
                        { label: translations.standard_week, value: 'WEEK' },
                        { label: translations.standard_month, value: 'MONTH' },
                        { label: translations.standard_year, value: 'YEAR' },
                    ];
                },
            })
            .addDependency(this.userService.currentUser$, {
                name: 'currentUser$',
                callback: (user) => {
                    this.licenseStatePro = user.isPro();
                    this.userDateFormat = user.getDateFormat();
                    this.currentUser = user;
                    this.firstDayOfWeek = user.getFirstDayOfWeek();
                    this.tryInitViewByLocalStorage(user);
                },
            })
            .addDependency(this.userService.account$, {
                name: 'account$',
                callback: (account) => {
                    const accountUsers = account.users;
                    this.accountUserIds = accountUsers.map((user) => user.id);
                },
            })
            .addDependency(this.layerService.layers$, {
                name: 'layers$',
                callback: (layers) => {
                    this.layers = layers;
                    if (layers.length > 0) {
                        this.topLayerName = layers[0].name ?? '';
                    }
                },
            })
            .addDependency(this.tileService.tiles$, {
                name: 'tiles$',
                callback: (tiles) => {
                    this.tiles = tiles;
                },
            })
            .onInit(async () => {
                await this.loadDashboardData(true, false);
            })
            .onChange(async () => {
                await this.loadDashboardData(false, false);
            })
            .initialize();

        this.subscriptions.add(
            this.loadingService.getLoadingState$(this.constructor.name).subscribe((loadingState: PageLoadingState) => {
                if (loadingState === 'initializing' || loadingState === 'data-loading') {
                    this.dataState = 'loading';
                } else if (loadingState === 'complete') {
                    const hasData =
                        (this.barData && this.barData.datasets?.length > 0) || this.todayUserData.length > 0;
                    this.dataState = hasData ? 'has-data' : 'no-data';
                } else {
                    console.error('Unexpected loading state: ' + loadingState);
                }
            }),
        );
    }

    private tryInitViewByLocalStorage(user: CurrentUser): void {
        if (this.viewStateInitialized) {
            return;
        }
        const view = LocalStorageService.getDashboardView(user.id);
        const validatedView = dashboardViewType(view);

        if (validatedView instanceof type.errors) {
            console.warn(
                'AnalyzeComponent.tryInitViewByLocalStorage() failed to parse dashboard view - going on with default',
                validatedView.summary,
            );
        } else {
            if (
                validatedView.selectedStateOption === 'Team' &&
                !user.hasPermission(UserPermission.ACTIVITY_READ_TEAM)
            ) {
                // reset to view to "me" as the users permissions to view team data have meanwhile been revoked
                validatedView.selectedStateOption = 'Me';
            }
            this.viewState = validatedView;
        }
        this.viewStateInitialized = true;
    }

    private saveViewState(): void {
        if (this.viewStateInitialized) {
            LocalStorageService.storeDashboardView(this.currentUser!.id, this.viewState);
        }
    }

    private async loadTodayActivities(): Promise<void> {
        const user = this.currentUser!;
        const dateRange = DateRangeHelper.getToday();
        const activityRequestDto: ActivityFiltersFormPageHack = {
            startDate: dateRange[0],
            endDate: dateRange[1],
            userIds: [user.id],
        };
        const filteredActivitiesResponse = await firstValueFrom(
            this.activityService.getFilteredActivities(activityRequestDto),
        );

        this.todayUserData = ActivityDashboadVmMapper.mapToVms(
            filteredActivitiesResponse.activities,
            user.settings.dateFormat,
            user.settings.timeFormat,
            this.tiles,
        );
    }

    private getUserIds(): number[] {
        if (this.viewState.selectedStateOption === 'Me') {
            return [this.currentUser!.id];
        }
        return this.accountUserIds;
    }

    private async loadCharts(): Promise<void> {
        const userIds = this.getUserIds();
        const layerIndex = 1;

        const start = DateRangeHelper.getStartDate(this.viewState.dateRange);
        const end = DateRangeHelper.getEndDate(this.viewState.dateRange);
        const diff = DateRangeHelper.getDurationDays(this.viewState.dateRange); // moment(end).diff(moment(start), 'days');

        if (diff > 84 && diff < 182) {
            this.barChartIntervalSelected = 'WEEK';
        } else if (diff >= 182) {
            this.barChartIntervalSelected = 'MONTH';
        } else {
            this.barChartIntervalSelected = 'DAY';
        }

        const chartVm = await firstValueFrom(
            this.activityService.getChart(layerIndex, start, end, this.barChartIntervalSelected, userIds),
        );
        this.setChart(chartVm);
    }

    /**
     * Loads the dashboard's data.
     * @param withToday If true, also loads today's activities.
     */
    private async loadDashboardData(withToday: boolean, showLoading = true): Promise<void> {
        if (!this.initMgr.dependenciesLoaded()) {
            return;
        }
        if (showLoading) {
            this.loadingService.setLoadingState('data-loading', this.constructor.name);
        }
        if (withToday) {
            await Promise.allSettled([this.loadCharts(), this.loadTodayActivities()]);
        } else {
            await this.loadCharts();
        }
        if (showLoading) {
            this.loadingService.setLoadingState('complete', this.constructor.name);
        }
    }

    public toggleUserOrTeam(value: DashboardViewMode): void {
        this.viewState.selectedStateOption = value;
        void this.loadDashboardData(false);
        this.saveViewState();
    }

    public onDateChanged(event: DateRangeChangedEvent): void {
        if (event.dateRange) {
            this.viewState.dateRange = event.dateRange;
            this.viewState.dateRangeOption = event.dateRangeOption;
            void this.loadDashboardData(false);
            this.saveViewState();
        }
    }

    getBoarder(color: string): string {
        return color.toLowerCase() === '#ffffffff' ? '1px solid #bbb' : color;
    }

    getLayerName(layerIndex: number): string {
        return this.layers.find((layer) => layer.index === layerIndex)?.name ?? '';
    }

    private formatDateLabels(dates: string[]): string[] {
        const dateFormat = this.currentUser?.getDateFormat()?.toUpperCase();
        if (!dateFormat) {
            return dates;
        }
        if (dates.length === 0) {
            return dates;
        }
        const checkDate = dates[0];
        const isIsoDate = (checkDate.length === 10 && checkDate.charAt(4) === '-') || checkDate.charAt(7) === '-';

        if (!isIsoDate) {
            return dates;
        }
        for (let i = 0; i < dates.length; i++) {
            const date = dates[i];
            dates[i] = moment(date).format(dateFormat);
        }
        return dates;
    }

    private setChart(chartVm: ChartVM): void {
        applyThemeColorsToCharts(this.themeService.colors());
        const dateLabels = this.formatDateLabels(chartVm.dates);
        this.barData = {
            labels: dateLabels,
            datasets: chartVm.barChart,
        };

        this.pieData = {
            labels: chartVm.pieChart.labels,
            datasets: [
                {
                    data: chartVm.pieChart.data,
                    backgroundColor: chartVm.pieChart.backgroundColor,
                    spacing: 0,
                    borderWidth: 1,
                    borderColor: this.themeService.colors().chartBorderColor,
                },
            ],
        };

        if (chartVm.totalSum) {
            this.totalTime = chartVm.totalSum;
        } else {
            this.totalTime = 0;
        }

        if (chartVm.highestName) {
            this.totalTimeName = chartVm.highestName;
        } else {
            this.totalTimeName = '-';
        }
    }

    downloadApp(): void {
        SharedFunctions.startAppDownload();

        this.hideAppDownloadDialog();
    }

    hideAppDownloadDialog(): void {
        this.displayAppDownloadModal = false;
        void this.router.navigate(['/']);
    }
}

export const dashboardViewModeType = type(`'Me' | 'Team'`);
export type DashboardViewMode = typeof dashboardViewModeType.infer;

export const dashboardViewType = type({
    dateRange: dateRangeType,
    dateRangeOption: [quickSelectRangeType, '|', 'undefined'],
    selectedStateOption: dashboardViewModeType,
});

export type DashboardView = typeof dashboardViewType.infer;
